import { useCallback, useEffect, useRef, useState } from 'react'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

const useColumnManagement = ({
  open,
  columns,
  boardId,
  onSave,
  setOpen,
  KANBAN_COLUMN_ROLE_FINALIZED,
  KANBAN_COLUMN_ROLE_CUSTOM,
  CUSTOM_COLUMN_LIMIT,
}) => {
  const snackbar = useSnackbar()

  const [isDirty, setIsDirty] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [localColumns, setLocalColumns] = useState([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [editData, setEditData] = useState({
    name: '',
    color: '',
    textColor: '',
  })
  const editNameRef = useRef(null)
  const finalizedDatesRef = useRef(null)

  useEffect(() => {
    if (open) {
      setLocalColumns(columns)
      setIsDirty(false)
      setEditingId(null)
      setShowAddForm(false)
    }
  }, [open]) // eslint-disable-line

  const handleStartEdit = (col) => {
    setEditingId(col.id)
    setEditData({
      name: col.title,
      color: col.color,
      textColor: col.textColor || '#FFFFFF',
    })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditData({ name: '', color: '', textColor: '' })
  }

  const handleSaveEdit = useCallback(
    async (id) => {
      const name = editNameRef.current?.value?.trim()
      if (!name) {
        snackbar.open({
          message: 'O nome da coluna é obrigatório',
          variant: 'error',
        })
        return
      }
      try {
        await service.dponet.kanbanColumns.update({
          kanbanBoardId: boardId,
          kanbanColumnId: id,
          title: name,
          color: editData.color,
          textColor: editData.textColor,
        })
        setLocalColumns((prev) => {
          const updated = prev.map((col) =>
            col.id === id
              ? {
                  ...col,
                  title: name,
                  color: editData.color,
                  textColor: editData.textColor,
                }
              : col,
          )
          onSave(updated)
          return updated
        })
        setIsDirty(true)
        setEditingId(null)
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      }
    },
    [boardId, editData.color, editData.textColor, onSave, snackbar],
  )

  const handleDelete = useCallback(
    async (id) => {
      try {
        await service.dponet.kanbanColumns.destroy({
          kanbanBoardId: boardId,
          kanbanColumnId: id,
        })
        setLocalColumns((prev) => {
          const updated = prev.filter((col) => col.id !== id)
          onSave(updated)
          return updated
        })
        setIsDirty(true)
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      }
    },
    [boardId, onSave, snackbar],
  )

  const handleAddColumn = useCallback(
    async (name, color, textColor) => {
      const trimmedName = name?.trim()
      if (!trimmedName) {
        snackbar.open({
          message: 'O nome da coluna é obrigatório',
          variant: 'error',
        })
        return
      }
      try {
        const response = await service.dponet.kanbanColumns.create({
          kanbanBoardId: boardId,
          title: trimmedName,
          color,
          textColor,
        })
        const created = response?.data?.kanbanColumn
        if (created) {
          setLocalColumns((prev) => {
            const finalizedIdx = prev.findIndex(
              (c) => c.role === KANBAN_COLUMN_ROLE_FINALIZED,
            )
            const updated = [...prev]
            if (finalizedIdx >= 0) {
              updated.splice(finalizedIdx, 0, created)
            } else {
              updated.push(created)
            }
            onSave(updated)
            return updated
          })
          setIsDirty(true)
        }
        setShowAddForm(false)
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      }
    },
    [boardId, snackbar, KANBAN_COLUMN_ROLE_FINALIZED],
  )

  const handleDragEnd = (result) => {
    if (!result.destination) return
    if (result.source.index === result.destination.index) return

    const maxDest =
      localColumns.findIndex((c) => c.role === KANBAN_COLUMN_ROLE_FINALIZED) - 1
    const clampedDest =
      maxDest >= 0
        ? Math.min(result.destination.index, maxDest)
        : result.destination.index

    const reordered = Array.from(localColumns)
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(clampedDest, 0, moved)

    const finalizedIdx = reordered.findIndex(
      (c) => c.role === KANBAN_COLUMN_ROLE_FINALIZED,
    )
    if (finalizedIdx !== -1 && finalizedIdx !== reordered.length - 1) {
      const [fin] = reordered.splice(finalizedIdx, 1)
      reordered.push(fin)
    }

    setLocalColumns(reordered)
    setIsDirty(true)
  }

  const handleCloseConfirmDelete = useCallback(
    (isOpen) => {
      if (!isOpen && !isDeleting) setConfirmDeleteId(null)
    },
    [isDeleting],
  )

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await handleDelete(confirmDeleteId)
    } finally {
      setIsDeleting(false)
      setConfirmDeleteId(null)
    }
  }, [confirmDeleteId, handleDelete])

  const handleSave = useCallback(async () => {
    try {
      const orderChanged = localColumns.some(
        (col, i) => col.id !== columns[i]?.id,
      )
      if (orderChanged) {
        await service.dponet.kanbanColumns.reorder({
          kanbanBoardId: boardId,
          ids: localColumns.map((c) => c.id),
        })
        onSave(localColumns)
      } else if (isDirty) {
        onSave(localColumns)
      }
      await finalizedDatesRef.current?.save()
      setOpen(false)
    } catch (error) {
      if (error.handled) return

      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    }
  }, [localColumns, columns, isDirty, onSave, snackbar, setOpen, boardId])

  const atLimit =
    localColumns.filter((c) => c.role === KANBAN_COLUMN_ROLE_CUSTOM).length >=
    CUSTOM_COLUMN_LIMIT

  return {
    isDirty,
    editingId,
    isDeleting,
    localColumns,
    showAddForm,
    setShowAddForm,
    confirmDeleteId,
    setConfirmDeleteId,
    editData,
    editNameRef,
    finalizedDatesRef,
    atLimit,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    setEditData,
    handleAddColumn,
    handleDragEnd,
    handleCloseConfirmDelete,
    handleConfirmDelete,
    handleSave,
  }
}

export default useColumnManagement
